import {Component, QueryList, TemplateRef, ViewChild, ViewChildren, ChangeDetectionStrategy} from "@angular/core";
import {AsyncPipe, formatDate, NgClass} from "@angular/common";
import {FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {SortableDirective, SortEvent} from "@app/_directives/sortable.directive";
import {
    Edi997Config,
    Edi997ConfigConnector,
    Edi997ConfigDetails,
    Edi997ConfigSettings,
    Edi997CreateRequest,
    PublishEdi997ConfigRequest,
    PublishEdi997ConfigResponse
} from "@app/_models/api";
import {GlobalService} from "@app/_services/global.service";
import {AuthService} from "@app/_services/auth.service";
import {ApiService} from "@app/_services/api.service";
import {ToastService} from "@app/_services/toast.service";
import {Edi997ConfigService} from "@app/_services/processing/edi-997-config.service";
import {BehaviorSubject, Observable} from "rxjs";
import {ActivatedRoute} from "@angular/router";
import {ModalConfirmComponent} from "@app/_components/modal-confirm/modal-confirm.component";
import {TableSearchComponent} from "@app/_components/table-search/table-search.component";
import {TablePagerComponent} from "@app/_components/table-pager/table-pager.component";

interface EditForm {
    version_notes: FormControl<string | null>;
    connectors: FormArray<FormGroup<SettingForm>>;
}

interface SettingForm {
    name: FormControl<string>;
    outbound_connector_id: FormControl<string>;
    outbound_folder: FormControl<string>;
    sender_ids: FormControl<string>;
    receiver_ids: FormControl<string>;
}

@Component({
    selector: "app-edi-997-config",
    imports: [
        TableSearchComponent,
        TablePagerComponent,
        AsyncPipe,
        FormsModule,
        ReactiveFormsModule,
        SortableDirective,
        NgClass,
        ModalConfirmComponent
    ],
    templateUrl: "./edi-997-config-component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: "./edi-997-config-component.scss"
})
export class Edi997ConfigComponent {
    @ViewChildren(SortableDirective) headers!: QueryList<SortableDirective>;
    savingData: boolean = false;
    total$: Observable<number>;
    configs$: Observable<Edi997Config[]>;
    private _configDetails$ = new BehaviorSubject<Edi997ConfigDetails | null>(null);
    configDetails$ = this._configDetails$.asObservable();
    private _loadingDetails$ = new BehaviorSubject<boolean>(false);
    loadingDetails$ = this._loadingDetails$.asObservable();

    availableConnectors: Edi997ConfigConnector[] = [];
    editForm = new FormGroup<EditForm>({
        version_notes: new FormControl("", Validators.required),
        connectors: new FormArray<FormGroup<SettingForm>>([])
    })
    protected readonly formatDate = formatDate;
    protected readonly Object = Object;
    @ViewChild("viewDetailsModal") private viewDetailsModal!: TemplateRef<any>;
    @ViewChild("editModal") private editModal!: TemplateRef<any>;
    @ViewChild("confirmationModal") private modalConfirmComponent!: ModalConfirmComponent;
    private viewModalRef!: NgbModalRef;
    private editModalRef!: NgbModalRef;

    constructor(
        public globalService: GlobalService,
        public authService: AuthService,
        private route: ActivatedRoute,
        private apiService: ApiService,
        private toastService: ToastService,
        public service: Edi997ConfigService,
        private modalService: NgbModal) {

        this.service.data = this.route.snapshot.data["resolvedData"].data;
        this.configs$ = this.service.data$;
        this.total$ = this.service.total$;
        this.service.refreshDataSource();
        this.loadConnectors();
    }

    get connectors() {
        return this.editForm.controls.connectors;
    }

    addConnector() {
        const newConnector = new FormGroup<SettingForm>({
            name: new FormControl("", {nonNullable: true, validators: [Validators.required]}),
            outbound_connector_id: new FormControl("", {nonNullable: true, validators: [Validators.required]}),
            outbound_folder: new FormControl("", {nonNullable: true, validators: [Validators.required]}),
            sender_ids: new FormControl("", {nonNullable: true, validators: [Validators.required]}),
            receiver_ids: new FormControl("", {nonNullable: true, validators: [Validators.required]})
        });

        this.editForm.controls.connectors.push(newConnector);
        this.editForm.markAsDirty();
    }

    removeConnector(i: number) {
        this.editForm.controls.connectors.removeAt(i);
        this.editForm.markAsDirty();
    }

    onView(id: string) {
        this._loadingDetails$.next(true);

        this.viewModalRef = this.modalService.open(this.viewDetailsModal, {
            modalDialogClass: "modal-dialog-centered modal-lg",
            backdrop: "static",
        });

        this.apiService.getEdi997ConfigDetails(id).subscribe({
            next: (details: Edi997ConfigDetails) => {
                this._configDetails$.next(details);
                this._loadingDetails$.next(false);
            },
            error: (error) => {
                this.toastService.showDanger("Error", "Failed to load configuration details");
                this._loadingDetails$.next(false);
                this.viewModalRef.dismiss();
                console.error("Error loading config details:", error);
            }
        });
    }

    onEdit(id: string) {
        this.editForm.reset();
        this.editForm.controls.connectors.clear();
        this._loadingDetails$.next(true);

        this.editModalRef = this.modalService.open(this.editModal, {
            modalDialogClass: "modal-dialog-centered modal-lg",
            backdrop: "static",
        });

        this.apiService.getEdi997ConfigDetails(id).subscribe({
            next: (details: Edi997ConfigDetails) => {
                // For each setting in the config, add a connector to the form
                if (details.configSettings) {
                    Object.keys(details.configSettings).forEach(key => {
                        const setting = details.configSettings[key];
                        const settingForm = new FormGroup<SettingForm>({
                            name: new FormControl(key, {nonNullable: true, validators: [Validators.required]}),
                            outbound_connector_id: new FormControl(setting.outbound_connector_id, {
                                nonNullable: true,
                                validators: [Validators.required]
                            }),
                            outbound_folder: new FormControl(setting.outbound_folder, {
                                nonNullable: true,
                                validators: [Validators.required]
                            }),
                            sender_ids: new FormControl(setting.sender_ids.join(","), {
                                nonNullable: true,
                                validators: [Validators.required]
                            }),
                            receiver_ids: new FormControl(setting.receiver_ids.join(","), {
                                nonNullable: true,
                                validators: [Validators.required]
                            })
                        });
                        this.editForm.controls.connectors.push(settingForm);
                    });
                }

                this._loadingDetails$.next(false);
            },
            error: (error) => {
                this.toastService.showDanger("Error", "Failed to load configuration details");
                this._loadingDetails$.next(false);
                console.error("Error loading config details:", error);
            }
        });
    }

    onSave(createAndPublish: boolean = false) {
        if (this.editForm.invalid) {
            this.toastService.showDanger("Invalid Data", "Please fill-in all required fields.");
            return;
        }
        this.globalService.showWaitOverlay(`Creating 997 config...`);
        this.savingData = true;
        const connectors = this.editForm.controls.connectors;
        const dict: Record<string, Edi997ConfigSettings> = {};

        for (let i = 0; i < connectors.length; i++) {
            const formGroup = connectors.at(i);
            const connector = formGroup.value;

            dict[connector.name!.trim()] = {
                name: connector.name!.trim(),
                outbound_connector_id: connector.outbound_connector_id!.trim(),
                outbound_folder: connector.outbound_folder!.trim(),
                sender_ids: this.getCleanIdArray(connector.sender_ids!),
                receiver_ids: this.getCleanIdArray(connector.receiver_ids!),
            };
        }

        const request: Edi997CreateRequest = {
            versionNotes: this.editForm.controls.version_notes.value!,
            configSettings: JSON.stringify(dict, null, 4),
            createAndPublish: createAndPublish
        };

        this.apiService.createEdi997Config(request).subscribe({
            next: (response: Edi997Config) => {
                this.toastService.showSuccess("Success", `Configuration created successfully${createAndPublish ? "and published" : ""}`);
                this.editModalRef.close();
                this.savingData = false;
                this.globalService.hideWaitOverlay();
                this.refreshGrid();
            },
            error: (error) => {
                this.toastService.showDanger("Error", "Failed to create configuration");
                this.savingData = false;
                this.globalService.hideWaitOverlay();
                console.error("Error creating config:", error);
            }
        });
    }

    onPublish(id: string) {
        this.modalConfirmComponent.open("Are you sure you want to publish this 997 config?");
        this.modalConfirmComponent.modalRef.result.then(
            () => {
                const request: PublishEdi997ConfigRequest = {id: id};
                this.handlePublish(request);
            },
            () => {
            }
        );
    }

    closeViewModal() {
        this.viewModalRef.close();
        this._configDetails$.next(null);
    }

    closeEditModal() {
        this.modalConfirmComponent.confirmIfDirty(this.editForm, () => this.editModalRef.close());
    }

    longFormatDate(input: string): string {
        return this.formatDate(input + "Z", "yyyy-MM-dd, hh:mm:ss a", "en-US")
    }

    onSort({column, direction}: SortEvent) {
        // resetting other headers
        for (const header of this.headers) {
            if (header.sortable !== column) {
                header.direction = "";
            }
        }

        this.service.sortColumn = column;
        this.service.sortDirection = direction;
    }

    private loadConnectors(): void {
        this.apiService.getEdi997ConfigConnectors().subscribe({
            next: (connectors: Edi997ConfigConnector[]) => {
                this.availableConnectors = connectors;
            },
            error: (error) => {
                this.toastService.showDanger("Error", "Failed to load connectors");
                console.error("Error loading connectors:", error);
            }
        });
    }

    private getCleanIdArray(input: string): string[] {
        // ensure each element in the array is trimmed of whitespace and is not empty
        return input.trim().split(",")
            .map(element => element.trim())
            .filter(element => element.length > 0);
    }

    private handlePublish(request: PublishEdi997ConfigRequest) {
        this.globalService.showWaitOverlay("Publishing 997 config...");

        this.apiService.publishEdi997Config(request).subscribe({
            next: (response: PublishEdi997ConfigResponse) => {
                this.globalService.hideWaitOverlay();
                this.toastService.showSuccess("Success", "Configuration published successfully");
                this.refreshGrid();
            },
            error: (error) => {
                this.toastService.showDanger("Error", "Failed to publish configuration");
                console.error("Error publishing config:", error);
            }
        });
    }

    private refreshGrid(): void {
        this.globalService.showWaitOverlay("Refreshing grid...");

        this.apiService.getEdi997Configs().subscribe({
            next: (configs: Edi997Config[]) => {
                this.service.data = configs;
                this.service.refreshDataSource();
                this.globalService.hideWaitOverlay();
            },
            error: (error) => {
                this.toastService.showDanger("Error", "Failed to refresh configurations");
                this.globalService.hideWaitOverlay();
                console.error("Error refreshing configs:", error);
            }
        });
    }
}
